import { useEffect, useMemo, useRef } from 'react';
import { useRouter } from 'next/router';
import { useListItemSelectContext } from '@context/negotiation-listings-split-view.context';
import { useGetSellingOfferProductById } from '@hooks/negotiations/useOffersListQueries';
import useWindowSize from '@hooks/use-window-size';
import { SellingOffersProductsItem } from '@interfaces/models/negotiation/domain/SellingOffersProduct';
import { SELLING_OFFERS_PATH } from '@constants/negotiation';

export const useSellingOffersNavigation = (sellingOffersProductsList: SellingOffersProductsItem[] = []) => {
  const { setSelectedListItemId, hasNegotiationColumnError, setHasNegotiationColumnError } = useListItemSelectContext();
  const hasInitialized = useRef(false);
  const router = useRouter();
  const { isWiderThanMd } = useWindowSize();
  const params = router.query.params;

  // Extract ID from URL
  const idFromUrl = useMemo(() => {
    const idString = Array.isArray(params) ? params[0] : undefined;
    return Number(idString);
  }, [router.query]);

  // Determine if we need to fetch a product by ID
  const shouldFetchProduct = idFromUrl && !hasNegotiationColumnError;
  // Fetch product if needed
  useGetSellingOfferProductById(shouldFetchProduct ? idFromUrl : null);

  // Handle default item selection
  const selectDefaultItem = () => {
    if (isWiderThanMd && sellingOffersProductsList?.length > 0) {
      const firstItemId = sellingOffersProductsList[0]?.product.id;
      if (firstItemId) {
        setSelectedListItemId(firstItemId);
        router.push(`${SELLING_OFFERS_PATH}${firstItemId}`, undefined, { shallow: true });
      }
    } else {
      setSelectedListItemId(null);
      router.push(SELLING_OFFERS_PATH, undefined, { shallow: true });
    }
  };

  // Initialize product selection
  useEffect(() => {
    if (sellingOffersProductsList?.length > 0) {
      if (!hasInitialized.current) {
        hasInitialized.current = true;
        if (idFromUrl) {
          setSelectedListItemId(idFromUrl);
          setHasNegotiationColumnError(false);
        } else {
          // Set error to true if ID is invalid format
          if (!!params && Number.isNaN(idFromUrl)) {
            setHasNegotiationColumnError(true);
          }
          selectDefaultItem();
        }
      }
    }
  }, [sellingOffersProductsList]);

  // Handle error state
  useEffect(() => {
    if (hasNegotiationColumnError) {
      selectDefaultItem();
    }
  }, [hasNegotiationColumnError]);
};
